import { useEffect, useRef, useState } from "react";

/**
 * Monitors audio level from a MediaStream using Web Audio AnalyserNode.
 * Returns a normalized level 0–1 and a boolean indicating if audio is clipping.
 */
export function useAudioLevel(stream: MediaStream | null, enabled: boolean) {
  const [level, setLevel] = useState(0);
  const contextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!stream || !enabled) {
      setLevel(0);
      return;
    }

    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) return;

    const ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.5;
    source.connect(analyser);

    contextRef.current = ctx;
    analyserRef.current = analyser;

    const data = new Uint8Array(analyser.frequencyBinCount);

    function tick() {
      if (!analyserRef.current) return;
      analyserRef.current.getByteFrequencyData(data);

      // Average the frequency data into a 0-1 range
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        sum += data[i];
      }
      const avg = sum / data.length / 255;
      setLevel(avg);

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      analyserRef.current = null;
      source.disconnect();
      ctx.close().catch(() => {});
      contextRef.current = null;
    };
  }, [stream, enabled]);

  return { level };
}
